/**
 * WebSyncStorage — browser implementation of SyncStorage.
 *
 * Wraps db.ts functions and raw SQLite operations. This adapter isolates
 * the sync engine from direct database dependencies so the engine can be
 * tested with mock storage.
 */

import type {
  Note,
  SyncChange,
  SyncOperation,
  SyncStatus,
  SyncStorage,
  Tag,
} from "../../types";
import {
  createCopyOfNote,
  deleteMeta,
  deleteSyncOp,
  enqueueSyncOp,
  getAllNotes,
  getAllSyncOps,
  getAllTags,
  getMeta,
  getNote,
  putNote,
  replaceAttachmentProjectionForNote,
  setMeta,
  extractNoteLinks,
  syncNoteLinksLocal,
  updateNote,
  updateSyncOpRetries,
  writeNoteLinks,
  upsertNotes,
  upsertTags,
  type LocalNote,
} from "../db";
import { execute, notifyChanges, query, queryOne, transaction } from "../sqlite";
import { clearInflightRecord, reconcileInflightRecords, writeInflightRecord, type InflightRecord } from "../inflightRecords";
import { Sentry } from "../sentry";
import {
  deduplicateSyncOps,
  downgradeToLegacy,
  hasUnsentText,
  identityOp,
  initialSyncState,
  isIdentityOp,
  isWellFormedOp,
  stripNoteTextFields,
  canUpgradeToV2,
  upgradeDecision,
  upgradeToV2,
  type NoteSyncState,
  type StoredNoteSync,
  type TextOp,
} from "@caliu/utils";
import {
  ENGINE_ROW_COLUMNS,
  getCatchUpIds,
  getSharingFeature,
  getSyncMode,
  getUpgradeDeferrals,
  hasUnsentWork,
  loadSyncState,
  readEngineRow,
  setCatchUpIds,
  setLiveFeature,
  setSharingFeature,
  setSyncMode,
  setUpgradeDeferrals,
  writeSyncState,
  type EngineRow,
  type SyncMode,
} from "../noteEngineState";
import { flushOpenEditor, hasOpenEditor, requestEditorReload } from "../openEditorRegistry";
import { isSharedWithMe, notifyAccess, type AccessNotice } from "../sharing";
import { isLiveNote } from "./liveNote";
import type { ContentSyncStore } from "./contentSync";

const SYNC_VERSION_KEY = "syncServerVersion";
/** The sync level this device's watermark was pulled at (`meta`, wiped on sign-out). */
const SYNC_LEVEL_KEY = "syncPullLevel";
/** `X-Caliu-Sync` of this build (api/client.ts). */
const PULL_LEVEL = "3";
/** `meta`: "1" while the next pull must start from 0 after a switch to legacy (`switchToLegacy`). */
const REFETCH_KEY = "syncLegacyRefetch";
const SYNC_WATERMARK_KEY = "lastSyncedAt";

/**
 * Fields an auto-merge writes itself. Everything else the replaced op carried
 * (pin, archive, read-only, manual order, and whatever is added later) rides
 * along on the re-enqueued op: a deny list on purpose, so a new metadata
 * field is carried without anyone remembering this function.
 */
const AUTO_MERGE_OWN_FIELDS = new Set([
  "content",
  "title",
  "word_count",
  "updated_at",
  "expected_updated_at",
]);

/**
 * The non-content fields of the queued op an auto-merge is about to replace.
 * `queued` is the whole queue; the note's `create`/`update` ops are folded the
 * way a push folds them (`deduplicateSyncOps`), so the result is exactly what
 * the conflicting push sent, plus anything queued since.
 */
export function autoMergeCarriedFields(
  queued: SyncOperation[],
  noteId: string,
): Record<string, unknown> {
  const forNote = queued.filter(
    (op) =>
      op.entity === "note" &&
      op.entityId === noteId &&
      (op.type === "update" || op.type === "create"),
  );
  const merged = deduplicateSyncOps(forNote).latest.get(`note:${noteId}`)?.data;
  const carried: Record<string, unknown> = {};
  for (const [key, value] of Object.entries((merged ?? {}) as Record<string, unknown>)) {
    if (!AUTO_MERGE_OWN_FIELDS.has(key)) carried[key] = value;
  }
  return carried;
}
function parseStoredOp(json: string | null): TextOp | null {
  if (json === null) return null;
  try {
    const op = JSON.parse(json) as unknown;
    return isWellFormedOp(op) ? op : null;
  } catch {
    return null;
  }
}

function toStoredNote(row: EngineRow): StoredNoteSync {
  return {
    id: row.id,
    rev: row.rev,
    base_content: row.base_content,
    base_updated_at: row.base_updated_at,
    content: row.content,
    title: row.title,
    inflight_client_id: row.inflight_client_id,
    inflight_seq: row.inflight_seq,
    inflight_base_rev: row.inflight_base_rev,
    inflight_ops: parseStoredOp(row.inflight_ops),
    pending_ops: parseStoredOp(row.pending_ops),
    sync_status: row.sync_status === "conflict" ? "conflict" : row.sync_status === "pending" ? "pending" : "synced",
    owner_id: row.owner_id,
  };
}

function writeStoredNote(n: StoredNoteSync): void {
  execute(
    `UPDATE notes SET rev = ?, base_content = ?, base_updated_at = ?, inflight_client_id = ?, inflight_seq = ?,
       inflight_base_rev = ?, inflight_ops = ?, pending_ops = ?, sync_status = ?
     WHERE id = ?`,
    [
      n.rev,
      n.base_content,
      n.base_updated_at,
      n.inflight_client_id,
      n.inflight_seq,
      n.inflight_base_rev,
      n.inflight_ops === null ? null : JSON.stringify(n.inflight_ops),
      n.pending_ops === null ? null : JSON.stringify(n.pending_ops),
      n.sync_status,
      n.id,
    ],
  );
}

/** A note with a text change the server has not confirmed (SQL, over `notes`). */
export const ENGINE_UNSENT_SQL =
  "(inflight_seq IS NOT NULL OR (pending_ops IS NOT NULL AND (pending_ops GLOB '*[\",-]*'" +
  " OR (base_content IS NULL AND content != '' AND sync_status != 'synced'))))";

/** Note ids whose `create` is still queued: outside the engine. */
function queuedNoteCreates(): Set<string> {
  return new Set(
    query<{ entity_id: string }>(
      "SELECT entity_id FROM sync_queue WHERE entity = 'note' AND type = 'create'",
    ).map((r) => r.entity_id),
  );
}

function hasQueuedNoteOps(noteId: string): boolean {
  return (
    (queryOne<{ cnt: number }>(
      "SELECT COUNT(*) AS cnt FROM sync_queue WHERE entity = 'note' AND entity_id = ?",
      [noteId],
    )?.cnt ?? 0) > 0
  );
}

/** A pulled note's rev, when the pull carries one (0 = unknown). */
const ROLES = new Set(["owner", "editor", "viewer"]);
const LINK_ACCESS = new Set(["none", "view", "edit"]);

/**
 * A pulled row's sharing fields (PLAN.md "Sharing data", Sync). A row without
 * them is an unshared note of this account: the columns clear.
 */
export function writeSharingFields(noteId: string, note: Record<string, unknown>): void {
  const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);
  const role = str(note.role);
  const link = str(note.linkAccess);
  execute(
    "UPDATE notes SET owner_id = ?, owner_name = ?, share_role = ?, member_count = ?, link_access = ? WHERE id = ?",
    [
      str(note.ownerId),
      str(note.ownerName),
      role && ROLES.has(role) ? role : null,
      typeof note.memberCount === "number" && note.memberCount > 0 ? Math.floor(note.memberCount) : 0,
      link && LINK_ACCESS.has(link) ? link : null,
      noteId,
    ],
  );
}

function pulledRev(note: Record<string, unknown>): number {
  return typeof note.rev === "number" && Number.isInteger(note.rev) && note.rev > 0 ? note.rev : 0;
}

export interface WebSyncStorageOptions {
  /** The note title for a text (the editor's rule, `extractTitleFromContent`). */
  deriveTitle?: (content: string) => string;
  /** The signed-in account (whose notes are its own). */
  getUserId?: () => string | null;
}

export class WebSyncStorage implements SyncStorage, ContentSyncStore {
  constructor(private _opts: WebSyncStorageOptions = {}) {}

  // ── Content sync engine (PLAN.md "Client engine", D8) ─────────────────

  getSyncMode(): SyncMode {
    return getSyncMode();
  }

  setLiveFeature(on: boolean): void {
    setLiveFeature(on);
  }

  setSharingFeature(on: boolean): void {
    setSharingFeature(on);
  }

  recordInflight(noteId: string, inflight: InflightRecord): Promise<void> {
    return writeInflightRecord(noteId, inflight);
  }

  /**
   * At load, before any sync: a change in flight the database export missed
   * (the tab closed before the debounced export) is put back from its record,
   * so it is resent under the same seq (`inflightRecords.ts`).
   */
  reconcileInflight(): Promise<void> {
    return reconcileInflightRecords(
      (noteId) => loadSyncState(noteId),
      (noteId, prev, next) => this.saveState(noteId, prev, next),
    );
  }

  /**
   * Legacy to v2, once per device (D8). One transaction. Waits (stays legacy)
   * while legacy note work it can deliver is left (`canUpgradeToV2`), at most
   * `MAX_UPGRADE_DEFERRALS` pulls (counted in `meta`), then runs anyway and
   * reports `sync_upgrade_forced`. `maxRetries` is the push's: an op past it
   * is never sent again.
   */
  switchToV2(maxRetries: number): void {
    let forced = null as { notes: number; ops: number } | null;
    transaction(() => {
      if (getSyncMode() === "v2") return;
      const rows = query<EngineRow>(`SELECT ${ENGINE_ROW_COLUMNS} FROM notes`);
      const queue = getAllSyncOps();
      const stored = rows.map(toStoredNote);
      const can = canUpgradeToV2(stored, queue, { maxRetries, meId: this._opts.getUserId?.() ?? null });
      const deferrals = getUpgradeDeferrals();
      const decision = upgradeDecision(can, deferrals);
      // Legacy note work left: stay legacy, the next pull tries again.
      if (decision === "defer") {
        setUpgradeDeferrals(deferrals + 1);
        return;
      }
      if (decision === "force") {
        forced = {
          notes: stored.filter((n) => n.sync_status === "pending").length,
          ops: queue.filter((op) => op.entity === "note").length,
        };
      }
      const r = upgradeToV2(stored, queue);
      for (const n of r.notes) writeStoredNote(n);
      const kept = new Map(r.queue.map((op) => [op.id, op]));
      for (const op of queue) {
        if (op.id === undefined) continue;
        const next = kept.get(op.id);
        if (!next) deleteSyncOp(op.id);
        else if (next !== op) {
          execute("UPDATE sync_queue SET data = ? WHERE id = ?", [JSON.stringify(next.data ?? null), op.id]);
        }
      }
      setCatchUpIds(new Set());
      setUpgradeDeferrals(0);
      setSyncMode("v2");
    });
    if (forced) {
      Sentry.captureMessage("sync_upgrade_forced", { level: "warning", extra: forced });
    }
    notifyChanges(["notes", "sync_queue", "meta"]);
  }

  /**
   * v2 to legacy (the flag went off, or `/sync/content` answered `disabled`).
   * One transaction. Unsent text legacy cannot queue (a note shared with this
   * account, or no common base) is saved as "Copy of …".
   */
  switchToLegacy(): void {
    let dropped: Array<{ id: string; clientId: string }> = [];
    transaction(() => {
      if (getSyncMode() === "legacy") return;
      const rows = query<EngineRow>(`SELECT ${ENGINE_ROW_COLUMNS} FROM notes WHERE pending_ops IS NOT NULL`);
      const stored = rows.map(toStoredNote);
      dropped = stored.flatMap((n) => (n.inflight_client_id ? [{ id: n.id, clientId: n.inflight_client_id }] : []));
      const r = downgradeToLegacy(stored, {
        now: new Date().toISOString(),
        timestamp: Date.now(),
        meId: this._opts.getUserId?.() ?? null,
      });
      // The copy first: it takes the title the note has now.
      for (const c of r.copies) createCopyOfNote(c.noteId, c.text);
      const before = new Map(stored.map((n) => [n.id, n]));
      for (const n of r.notes) {
        writeStoredNote(n);
        if (n.content !== before.get(n.id)?.content) this._writeText(n.id, n.content);
      }
      // A row with nothing unsent holds the server's text: its `updated_at` goes
      // back to the server's, so a later legacy edit sends the
      // `expected_updated_at` the server holds (v2 stamps local times).
      for (const n of stored) {
        const copied = r.copies.some((c) => c.noteId === n.id);
        if ((hasUnsentText(n) && !copied) || n.base_updated_at === "") continue;
        execute("UPDATE notes SET updated_at = ? WHERE id = ?", [n.base_updated_at, n.id]);
      }
      for (const op of r.enqueue) enqueueSyncOp(op);
      // A copied note keeps text the server may not have, and a note waiting
      // for its catch-up holds stale text, both marked synced: the next legacy
      // pull starts from 0 (once) so legacy rewrites them with the server's.
      if (r.copies.length > 0 || getCatchUpIds().size > 0) setMeta(REFETCH_KEY, "1");
      setCatchUpIds(new Set());
      setUpgradeDeferrals(0);
      setSyncMode("legacy");
    });
    // The changes in flight were dropped: their records go.
    for (const n of dropped) clearInflightRecord(n.id, n.clientId);
    notifyChanges(["notes", "sync_queue", "meta", "note_links", "attachment_projection"]);
  }

  /** Writes a note's text and what is derived from it (title, word count, links). */
  private _writeText(noteId: string, content: string): void {
    const row = queryOne<{ user_id: string; created_at: string; updated_at: string }>(
      "SELECT user_id, created_at, updated_at FROM notes WHERE id = ?",
      [noteId],
    );
    if (!row) return;
    const title = this._opts.deriveTitle?.(content);
    execute(
      `UPDATE notes SET content = ?, word_count = ?${title !== undefined ? ", title = ?" : ""} WHERE id = ?`,
      [content, (content || "").split(/\s+/).filter(Boolean).length, ...(title !== undefined ? [title] : []), noteId],
    );
    replaceAttachmentProjectionForNote({
      id: noteId,
      userId: row.user_id,
      content,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    });
    writeNoteLinks(noteId, extractNoteLinks(noteId, content));
  }

  contentCandidates(): string[] {
    const creates = queuedNoteCreates();
    const catchUp = getCatchUpIds();
    const rows = query<{ id: string; rev: number; base_content: string | null; inflight_seq: number | null; pending_ops: string }>(
      "SELECT id, rev, base_content, inflight_seq, pending_ops FROM notes WHERE pending_ops IS NOT NULL",
    );
    const out: string[] = [];
    for (const r of rows) {
      // A queued create stays outside; a live note's text rides its socket.
      if (creates.has(r.id) || isLiveNote(r.id)) continue;
      const buffer = parseStoredOp(r.pending_ops);
      if (
        r.inflight_seq !== null ||
        r.rev === 0 ||
        r.base_content === null ||
        catchUp.has(r.id) ||
        (buffer !== null && !isIdentityOp(buffer))
      ) {
        out.push(r.id);
      }
    }
    return out;
  }

  loadState(noteId: string): NoteSyncState | null {
    return loadSyncState(noteId);
  }

  saveState(noteId: string, prev: NoteSyncState, next: NoteSyncState): void {
    const textChanged = next.content !== prev.content;
    const row = textChanged
      ? queryOne<{ user_id: string; created_at: string; updated_at: string }>(
          "SELECT user_id, created_at, updated_at FROM notes WHERE id = ?",
          [noteId],
        )
      : null;
    transaction(() => {
      if (row) {
        const title = this._opts.deriveTitle?.(next.content);
        const updatedAt =
          next.baseUpdatedAt && next.baseUpdatedAt > row.updated_at ? next.baseUpdatedAt : row.updated_at;
        execute(
          `UPDATE notes SET content = ?, word_count = ?, updated_at = ?${title !== undefined ? ", title = ?" : ""} WHERE id = ?`,
          [
            next.content,
            (next.content || "").split(/\s+/).filter(Boolean).length,
            updatedAt,
            ...(title !== undefined ? [title] : []),
            noteId,
          ],
        );
        replaceAttachmentProjectionForNote({
          id: noteId,
          userId: row.user_id,
          content: next.content,
          createdAt: row.created_at,
          updatedAt,
        });
        writeNoteLinks(noteId, extractNoteLinks(noteId, next.content));
      }
      writeSyncState(noteId, next);
      // The change in flight landed (or was dropped): its record goes.
      if (prev.inflight && next.inflight?.seq !== prev.inflight.seq) clearInflightRecord(noteId, prev.inflight.clientId);
      const status = queryOne<{ sync_status: string }>("SELECT sync_status FROM notes WHERE id = ?", [noteId])?.sync_status;
      const pending = hasUnsentWork(next, status ?? "synced") || hasQueuedNoteOps(noteId);
      execute("UPDATE notes SET sync_status = ? WHERE id = ?", [pending ? "pending" : "synced", noteId]);
    });
    // The open editor takes the stored text (it was flushed before, rule 2):
    // in place while live, else a reload.
    if (textChanged) requestEditorReload(noteId);
    notifyChanges(textChanged ? ["notes", "note_links", "attachment_projection"] : ["notes"]);
  }

  noteTitle(noteId: string): string | undefined {
    return queryOne<{ title: string }>("SELECT title FROM notes WHERE id = ?", [noteId])?.title;
  }

  addCatchUp(noteIds: string[]): void {
    if (noteIds.length === 0) return;
    const ids = getCatchUpIds();
    for (const id of noteIds) ids.add(id);
    setCatchUpIds(ids);
  }

  clearCatchUp(noteId: string): void {
    const ids = getCatchUpIds();
    if (ids.delete(noteId)) setCatchUpIds(ids);
  }

  /**
   * The local text of a note that shares no base with the server's (an
   * orphan, PLAN.md "Snapshots") becomes a new private note: "Copy of …".
   */
  createOrphanCopy(noteId: string, text: string): void {
    createCopyOfNote(noteId, text);
  }

  /**
   * A v2 create landed: the note enters the engine at the reply's `{rev, text}`
   * (PLAN.md "Creates") when that is rev 1, the text this device created. A
   * reply at a later rev carries the head, which is not what the local text
   * was edited from: no base, so the next pass catches up from rev 0 (a
   * snapshot, an orphan copy if the texts differ). Without a reply, at rev 0
   * over the text pushed. Updates queued meanwhile lose their text fields: it
   * goes through `/sync/content` now. The mode is read here, as it writes.
   */
  enterEngineAfterCreate(
    noteId: string,
    reply: { rev?: number; text?: string },
    pushed: { content: string; updatedAt: string },
  ): void {
    if (getSyncMode() !== "v2") return;
    transaction(() => {
      const row = readEngineRow(noteId);
      if (!row || row.pending_ops !== null || queuedNoteCreates().has(noteId)) return;
      const hasReply = typeof reply.rev === "number" && typeof reply.text === "string";
      const s = hasReply
        ? initialSyncState({
            rev: reply.rev as number,
            base: reply.rev === 1 ? (reply.text as string) : null,
            baseUpdatedAt: pushed.updatedAt,
            content: row.content,
          })
        : initialSyncState({ rev: 0, base: pushed.content, baseUpdatedAt: pushed.updatedAt, content: row.content });
      writeSyncState(noteId, s);
      for (const op of getAllSyncOps()) {
        if (op.entity !== "note" || op.entityId !== noteId || op.id === undefined) continue;
        const next = stripNoteTextFields(op);
        if (!next) deleteSyncOp(op.id);
        else if (next !== op) {
          execute("UPDATE sync_queue SET data = ? WHERE id = ?", [JSON.stringify(next.data ?? null), op.id]);
        }
      }
    });
    notifyChanges(["notes", "sync_queue"]);
  }

  async getAllSyncOps(): Promise<SyncOperation[]> {
    return getAllSyncOps();
  }

  async deleteSyncOp(id: number): Promise<void> {
    deleteSyncOp(id);
  }

  async updateSyncOpRetries(id: number, retries: number): Promise<void> {
    updateSyncOpRetries(id, retries);
  }

  async upsertNotes(notes: Note[]): Promise<void> {
    upsertNotes(notes);
  }

  async upsertTags(tags: Tag[]): Promise<void> {
    upsertTags(tags);
  }

  async getLastSyncTimestamp(): Promise<string | null> {
    return getMeta(SYNC_WATERMARK_KEY);
  }

  async setLastSyncTimestamp(ts: string): Promise<void> {
    setMeta(SYNC_WATERMARK_KEY, ts);
  }

  async updateNoteSyncStatus(noteId: string, status: SyncStatus): Promise<void> {
    execute("UPDATE notes SET sync_status = ? WHERE id = ?", [status, noteId]);
    notifyChanges(["notes"]);
  }

  async bulkDeleteNotes(ids: string[]): Promise<void> {
    transaction(() => {
      for (const id of ids) {
        execute("DELETE FROM note_tags WHERE note_id = ?", [id]);
        execute("DELETE FROM note_links WHERE source_note_id = ? OR target_note_id = ?", [id, id]);
        execute("DELETE FROM notes WHERE id = ?", [id]);
      }
    });
    notifyChanges([
      "notes",
      "note_tags",
      "note_links",
      "attachment_projection",
    ]);
  }

  async getAllLocalTags(): Promise<Array<Tag & { syncStatus?: SyncStatus }>> {
    return getAllTags();
  }

  async bulkDeleteTags(ids: string[]): Promise<void> {
    transaction(() => {
      for (const id of ids) {
        execute("DELETE FROM note_tags WHERE tag_id = ?", [id]);
        execute("DELETE FROM tags WHERE id = ?", [id]);
      }
    });
    notifyChanges(["tags", "note_tags"]);
  }

  /** This pull started from 0 for the level 3 re-pull: its apply records the level. */
  private _levelRepull = false;
  /** This pull started from 0 for the re-fetch after a switch to legacy: its apply clears the flag. */
  private _refetchPull = false;

  async getServerVersion(): Promise<number> {
    // Pulls below level 3 skipped rows of notes shared with this account but
    // still moved the watermark past them (PLAN.md D9). Those rows only exist
    // once sharing is on for the account, so the first pull after
    // `collabFeatures.sharing` turns on starts again from 0, once per account
    // and device. Before that, no account pays for a full re-pull.
    const level = getSharingFeature() && getMeta(SYNC_LEVEL_KEY) !== PULL_LEVEL;
    // Set by `switchToLegacy` when it left rows with text the server may not have.
    const refetch = getMeta(REFETCH_KEY) === "1";
    if (level || refetch) {
      this._levelRepull = level;
      this._refetchPull = refetch;
      return 0;
    }
    const versionStr = getMeta(SYNC_VERSION_KEY);
    return versionStr ? parseInt(versionStr, 10) : 0;
  }

  async setServerVersion(version: number): Promise<void> {
    setMeta(SYNC_VERSION_KEY, String(version));
  }

  // Only flip `pending` → `synced` when no queued op remains for the entity.
  // The push loop deletes the op it just pushed BEFORE marking, so a
  // remaining row can only be an op enqueued WHILE the push request was in
  // flight (autosave during sync). Flipping to `synced` in that race erased
  // the pending flag that protects the note from the next pull's server
  // echo, letting it transiently overwrite the newer local content.
  // Mirrored in Swift's NativeSyncStorage — change both or neither.
  async markEntitySynced(entity: string, entityId: string): Promise<void> {
    const table = entity === "note" ? "notes" : "tags";
    execute(
      `UPDATE ${table} SET sync_status = 'synced'
       WHERE id = ? AND sync_status = 'pending'
         AND NOT EXISTS (
           SELECT 1 FROM sync_queue sq
           WHERE sq.entity = ? AND sq.entity_id = ${table}.id
         )`,
      [entityId, entity],
    );
    notifyChanges([table]);
  }

  async getNote(id: string): Promise<LocalNote | undefined> {
    return getNote(id);
  }

  async reconcileTagId(clientId: string, serverId: string): Promise<void> {
    transaction(() => {
      const serverTag = queryOne<{ id: string }>(
        "SELECT id FROM tags WHERE id = ?",
        [serverId],
      );

      if (serverTag) {
        execute("UPDATE OR IGNORE note_tags SET tag_id = ? WHERE tag_id = ?", [
          serverId,
          clientId,
        ]);
        execute("DELETE FROM note_tags WHERE tag_id = ?", [clientId]);
        execute("UPDATE tags SET parent_id = ? WHERE parent_id = ?", [
          serverId,
          clientId,
        ]);
        execute("DELETE FROM tags WHERE id = ?", [clientId]);
      } else {
        execute(
          `INSERT INTO tags (id, user_id, name, parent_id, color, icon, is_pinned, created_at, sync_status)
           SELECT ?, user_id, name, parent_id, color, icon, is_pinned, created_at, 'synced' FROM tags WHERE id = ?`,
          [serverId, clientId],
        );
        execute("UPDATE note_tags SET tag_id = ? WHERE tag_id = ?", [
          serverId,
          clientId,
        ]);
        execute("UPDATE tags SET parent_id = ? WHERE parent_id = ?", [
          serverId,
          clientId,
        ]);
        execute("DELETE FROM tags WHERE id = ?", [clientId]);
      }

      // Update pending sync queue ops that reference the old client tag ID
      const pendingOps = query<{ id: number; data: string | null }>(
        "SELECT id, data FROM sync_queue WHERE data IS NOT NULL",
      );
      for (const row of pendingOps) {
        if (!row.data || !row.data.includes(clientId)) continue;
        const updated = row.data.replaceAll(clientId, serverId);
        execute("UPDATE sync_queue SET data = ? WHERE id = ?", [updated, row.id]);
      }
      execute(
        "UPDATE sync_queue SET entity_id = ? WHERE entity = 'tag' AND entity_id = ?",
        [serverId, clientId],
      );
    });
    notifyChanges(["tags", "note_tags"]);
  }

  async deleteSupersededOps(ops: SyncOperation[]): Promise<void> {
    for (const op of ops) {
      if (op.id != null) {
        deleteSyncOp(op.id);
      }
    }
  }

  async applyPullChanges(changes: SyncChange[], serverVersion: number, userId: string): Promise<void> {
    // Apply notes and tags before note_tags to satisfy FK constraints.
    // shared_note_ref is independent and ordered last for cosmetic consistency.
    const entityOrder: Record<string, number> = {
      note: 0,
      tag: 0,
      note_tag: 1,
      shared_note_ref: 2,
    };
    const sorted = [...changes].sort(
      (a, b) => (entityOrder[a.entity] ?? 0) - (entityOrder[b.entity] ?? 0),
    );
    const uid = userId;
    const v2 = getSyncMode() === "v2";
    const catchUp: string[] = [];
    const notices: AccessNotice[] = [];
    // Typing in an open note that is about to go (access lost) is saved first,
    // so its copy has it (caller rule 2).
    for (const change of sorted) {
      if (change.entity === "note" && change.operation === "delete") flushOpenEditor(change.entityId);
    }
    transaction(() => {
      for (const change of sorted) {
        if (change.entity === "note" && change.operation === "delete") {
          const notice = this._keepUnsentOnAccessLoss(change.entityId, uid);
          if (notice) notices.push(notice);
        }
        if (change.entity === "note") {
          if (v2) this._applyNoteChangeV2(change, uid, catchUp);
          else this._applyNoteChange(change, uid);
        } else if (change.entity === "tag") {
          this._applyTagChange(change, uid);
        } else if (change.entity === "note_tag") {
          this._applyNoteTagChange(change);
        } else if (change.entity === "shared_note_ref") {
          this._applySharedNoteRefChange(change, uid);
        }
      }
      setMeta(SYNC_VERSION_KEY, String(serverVersion));
      if (this._levelRepull) setMeta(SYNC_LEVEL_KEY, PULL_LEVEL);
      if (this._refetchPull) deleteMeta(REFETCH_KEY);
      if (catchUp.length > 0) this.addCatchUp(catchUp);
    });
    this._levelRepull = false;
    this._refetchPull = false;
    for (const n of notices) notifyAccess(n);
    notifyChanges([
      "notes",
      "tags",
      "note_tags",
      "note_links",
      "attachment_projection",
      "shared_note_refs",
      "shared_notes",
    ]);
  }

  async markNoteConflict(noteId: string): Promise<void> {
    execute("UPDATE notes SET sync_status = ? WHERE id = ?", ["conflict", noteId]);
    notifyChanges(["notes"]);
  }

  async getNoteBaseContent(noteId: string): Promise<string | null> {
    const row = queryOne<{ base_content: string | null }>(
      "SELECT base_content FROM notes WHERE id = ?",
      [noteId],
    );
    return row?.base_content ?? null;
  }

  async applyAutoMergedNote(
    noteId: string,
    mergedContent: string,
    expectedUpdatedAt: string,
  ): Promise<void> {
    await this.applyAutoMergedNoteIfUnchanged(noteId, mergedContent, expectedUpdatedAt, null);
  }

  /**
   * `applyAutoMergedNote`, guarded: `mergedFrom` is the local text the merge
   * was computed from. When the row no longer holds it (a save landed between
   * the engine's read and this write) NOTHING is written and the answer is
   * false: the engine merges again from the row. Writing would overwrite that
   * save with a merge that does not contain it. `null` skips the check.
   * Not part of `SyncStorage` (`@caliu/types`): the engine uses it when the
   * storage has it.
   */
  async applyAutoMergedNoteIfUnchanged(
    noteId: string,
    mergedContent: string,
    expectedUpdatedAt: string,
    mergedFrom: string | null,
  ): Promise<boolean> {
    const now = new Date().toISOString();
    let written = false;
    // One transaction: the check, the merged text, the queue it replaces and
    // the op that carries it land together or not at all.
    transaction(() => {
      if (mergedFrom !== null && getNote(noteId)?.content !== mergedFrom) return;
      written = true;
      // Read BEFORE anything is enqueued. The op that conflicted (and the ops
      // it absorbed) is still in the queue, and so is any update queued WHILE
      // that push was in flight (a keystroke, a pin): it was not in the push's
      // snapshot, so the engine will not delete it. A pin, archive or
      // read-only toggle on any of them is carried over; re-enqueueing the
      // content alone dropped it, and the next pull of the note then cleared
      // it on this device too.
      const queued = getAllSyncOps();
      const carried = autoMergeCarriedFields(queued, noteId);
      // Write the merged content locally (also refreshes the attachment
      // projections) and re-sync note links — mirrors the autosave save path.
      updateNote(noteId, {
        content: mergedContent,
        wordCount: (mergedContent || "").split(/\s+/).filter(Boolean).length,
        updatedAt: now,
        syncStatus: "pending",
      });
      syncNoteLinksLocal(noteId, mergedContent);
      // Every queued update of the note goes: this op replaces them all. One
      // left behind (the update queued during the push) would be folded with
      // this op by the next push, which keeps the EARLIEST
      // expected_updated_at: its stale one. The merged text would then be
      // pushed against a server version it was never merged with.
      for (const op of queued) {
        if (op.entity === "note" && op.entityId === noteId && op.type === "update" && op.id !== undefined) {
          deleteSyncOp(op.id);
        }
      }
      // Re-enqueue as a normal update op. expected_updated_at is the server's
      // current timestamp (theirs), so the re-push applies cleanly — or, if a
      // third edit landed meanwhile, it re-conflicts and we re-merge (the loop
      // terminates because each round consumes the newest server state).
      // ponytail: title not re-derived here; the next pull's _applyNoteChange
      // sets it from the server. The merged content is correct immediately; the
      // title (first readable line, rarely touched by a merge) self-heals in one
      // sync cycle.
      enqueueSyncOp({
        type: "update",
        entity: "note",
        entityId: noteId,
        data: {
          ...carried,
          content: mergedContent,
          updated_at: now,
          expected_updated_at: expectedUpdatedAt,
        },
        timestamp: Date.now(),
        retries: 0,
      });
    });
    return written;
  }

  /**
   * "Keep Server": drop the note's queued ops and write the server's note as
   * `synced`, in ONE transaction. Apart, a failure between the two leaves
   * either a synced note with a stale op behind it (which the next push sends
   * over the version just kept) or a conflict with its ops gone.
   * Not part of `SyncStorage` (`@caliu/types`): the engine uses it when the
   * storage has it.
   */
  async keepServerNote(note: Note & { syncStatus: SyncStatus }, opIds: number[]): Promise<void> {
    transaction(() => {
      for (const id of opIds) deleteSyncOp(id);
      putNote(note as LocalNote);
    });
  }

  async putNote(note: Note & { syncStatus: SyncStatus }): Promise<void> {
    putNote(note as LocalNote);
  }

  async markNoteSynced(noteId: string): Promise<void> {
    // A pushed note is now confirmed on the server, so its current content is
    // the new merge ancestor (base) for future three-way conflict resolution.
    execute(
      // Outside the engine only: the engine alone moves a v2 note's base. A
      // new base has no known rev.
      `UPDATE notes SET sync_status = ?,
         base_content = CASE WHEN pending_ops IS NULL THEN content ELSE base_content END,
         rev = CASE WHEN pending_ops IS NULL THEN 0 ELSE rev END
       WHERE id = ?`,
      ["synced", noteId],
    );
    notifyChanges(["notes"]);
  }

  async getAllNotes(): Promise<Array<Note & { syncStatus: SyncStatus }>> {
    return getAllNotes();
  }

  async getPendingCount(): Promise<number> {
    const row = queryOne<{ cnt: number }>(
      "SELECT COUNT(*) as cnt FROM sync_queue",
    );
    return row?.cnt ?? 0;
  }

  async getFailedCount(maxRetries: number): Promise<number> {
    const row = queryOne<{ cnt: number }>(
      "SELECT COUNT(*) as cnt FROM sync_queue WHERE retries >= ?",
      [maxRetries],
    );
    return row?.cnt ?? 0;
  }

  async retryFailedOps(maxRetries: number): Promise<void> {
    execute("UPDATE sync_queue SET retries = 0 WHERE retries >= ?", [maxRetries]);
  }

  async clearFailedOps(maxRetries: number): Promise<void> {
    execute("DELETE FROM sync_queue WHERE retries >= ?", [maxRetries]);
    notifyChanges(["sync_queue"]);
  }

  async getConflictCount(): Promise<number> {
    const row = queryOne<{ cnt: number }>(
      "SELECT COUNT(*) as cnt FROM notes WHERE sync_status = 'conflict'",
    );
    return row?.cnt ?? 0;
  }

  async cleanupZombiePendingStates(): Promise<void> {
    const zombieNotes = query<{ id: string }>(
      `SELECT n.id FROM notes n
       WHERE n.sync_status = 'pending'
         AND NOT ${ENGINE_UNSENT_SQL}
         AND NOT EXISTS (
           SELECT 1 FROM sync_queue sq
           WHERE sq.entity = 'note' AND sq.entity_id = n.id
         )`,
    );

    const zombieTags = query<{ id: string }>(
      `SELECT t.id FROM tags t
       WHERE t.sync_status = 'pending'
         AND NOT EXISTS (
           SELECT 1 FROM sync_queue sq
           WHERE sq.entity = 'tag' AND sq.entity_id = t.id
         )`,
    );

    if (zombieNotes.length > 0 || zombieTags.length > 0) {
      for (const n of zombieNotes) {
        await this.markEntitySynced("note", n.id);
      }
      for (const t of zombieTags) {
        await this.markEntitySynced("tag", t.id);
      }
      console.debug(
        `[Caliu] Cleaned up zombie pending states: ${zombieNotes.length} notes, ${zombieTags.length} tags`,
      );
    }
  }

  // ── Private helpers for applying individual changes ──

  /**
   * A note shared with this account is going (removed, left elsewhere, the
   * link turned off, the owner's trash): text this device has not sent is
   * saved as "Copy of …" first (PLAN.md "Rules"). Returns the notice to show.
   */
  private _keepUnsentOnAccessLoss(noteId: string, userId: string): AccessNotice | null {
    const row = queryOne<{ owner_id: string | null; content: string; base_content: string | null; sync_status: string }>(
      "SELECT owner_id, content, base_content, sync_status FROM notes WHERE id = ?",
      [noteId],
    );
    if (!row || !isSharedWithMe({ ownerId: row.owner_id ?? undefined }, userId)) return null;
    const s = loadSyncState(noteId);
    const unsent = s
      ? hasUnsentWork(s, row.sync_status)
      : row.sync_status !== "synced" && row.content !== (row.base_content ?? "");
    if (unsent) this.createOrphanCopy(noteId, s?.content ?? row.content);
    if (!unsent && !hasOpenEditor(noteId)) return null;
    return { kind: "removed", noteId, savedCopy: unsent };
  }

  onUnsentSavedAsCopy(noteId: string): void {
    notifyAccess({ kind: "savedCopy", noteId });
  }

  /** `isSharedWithMe` for the content driver: a note this account does not own. */
  isSharedWithMe(noteId: string): boolean {
    const row = queryOne<{ owner_id: string | null }>("SELECT owner_id FROM notes WHERE id = ?", [noteId]);
    return isSharedWithMe({ ownerId: row?.owner_id ?? undefined }, this._opts.getUserId?.() ?? null);
  }

  /** The role stored for the note (`share_role`), for the content driver. */
  shareRole(noteId: string): "owner" | "editor" | "viewer" | null {
    const row = queryOne<{ share_role: string | null }>("SELECT share_role FROM notes WHERE id = ?", [noteId]);
    const role = row?.share_role;
    return role === "owner" || role === "editor" || role === "viewer" ? role : null;
  }

  private _applyNoteChange(change: SyncChange, userId: string): void {
    const { entityId, operation, data } = change;

    if (operation === "delete") {
      execute("DELETE FROM note_tags WHERE note_id = ?", [entityId]);
      execute(
        "DELETE FROM note_links WHERE source_note_id = ? OR target_note_id = ?",
        [entityId, entityId],
      );
      execute("DELETE FROM notes WHERE id = ?", [entityId]);
      return;
    }

    const existing = queryOne<{ sync_status: string; user_id: string }>(
      "SELECT sync_status, user_id FROM notes WHERE id = ?",
      [entityId],
    );
    // Skip if local note has pending changes — they'll be pushed up.
    // Also skip when the note is in `conflict` state: push has detected
    // the server diverged, the queued op was removed, and the user must
    // resolve via `resolveConflict()`. Silently applying the server
    // snapshot here would overwrite the user's local edits — exactly the
    // "note reverted to an old version" symptom on multi-device setups,
    // including frontmatter that lives in note content.
    if (existing?.sync_status === "pending" || existing?.sync_status === "conflict") {
      // Who the note is whose and the person's role are the server's alone.
      writeSharingFields(entityId, data as Record<string, unknown>);
      return;
    }

    const existingUserId = existing?.user_id || userId;
    const note = data as Record<string, unknown>;
    const content = (note.content ?? "") as string;
    // Upsert that UPDATEs in place on conflict — must NOT be `INSERT OR
    // REPLACE`. SQLite implements REPLACE as DELETE + INSERT, and the deleted
    // row fires `note_tags ... ON DELETE CASCADE`, wiping every tag link this
    // note has. A pull that echoes the note back (e.g. right after we pushed an
    // edit) carries no note_tag changes, so the links never come back and the
    // note silently drops out of all its tag filters. `ON CONFLICT DO UPDATE`
    // mutates the existing row instead, leaving note_tags intact.
    execute(
      `INSERT INTO notes (id, user_id, title, content, is_pinned, is_encrypted, encryption_hint, word_count, created_at, updated_at, deleted_at, archived_at, shared_at, sort_order, base_content, is_read_only, sync_status, rev, base_updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'synced', ?, ?)
       ON CONFLICT(id) DO UPDATE SET
         user_id = excluded.user_id,
         title = excluded.title,
         content = excluded.content,
         is_pinned = excluded.is_pinned,
         is_encrypted = excluded.is_encrypted,
         encryption_hint = excluded.encryption_hint,
         word_count = excluded.word_count,
         created_at = excluded.created_at,
         updated_at = excluded.updated_at,
         deleted_at = excluded.deleted_at,
         archived_at = excluded.archived_at,
         shared_at = excluded.shared_at,
         sort_order = excluded.sort_order,
         base_content = excluded.base_content,
         is_read_only = excluded.is_read_only,
         sync_status = 'synced',
         rev = excluded.rev,
         base_updated_at = excluded.base_updated_at`,
      [
        entityId,
        existingUserId,
        note.title ?? "",
        content,
        note.isPinned ? 1 : 0,
        note.isEncrypted ? 1 : 0,
        note.encryptionHint ?? null,
        note.wordCount ?? 0,
        note.createdAt ?? new Date().toISOString(),
        note.updatedAt ?? new Date().toISOString(),
        note.deletedAt ?? null,
        note.archivedAt ?? null,
        note.sharedAt ?? null,
        (note.sortOrder ?? null) as number | null,
        // Server content just landed → it is the new merge ancestor.
        content,
        note.isReadOnly ? 1 : 0,
        // Recorded for the switch to v2 (D8). A pull that carries no rev
        // leaves it unknown (0): the first v2 catch-up gets a snapshot.
        pulledRev(note),
        (note.updatedAt ?? "") as string,
      ],
    );

    writeSharingFields(entityId, note);

    replaceAttachmentProjectionForNote({
      id: entityId,
      userId: existingUserId,
      content,
      createdAt: (note.createdAt ?? new Date().toISOString()) as string,
      updatedAt: (note.updatedAt ?? new Date().toISOString()) as string,
    });

    // Rebuild note_links from the pulled content (no nested transaction or
    // per-note notifyChanges — applyPullChanges handles both at batch level)
    writeNoteLinks(entityId, extractNoteLinks(entityId, content));
  }

  /**
   * A pulled note in v2 mode. The text belongs to the engine: a pull never
   * writes `content` of a note this device has. Its metadata lands as in
   * legacy (not over queued note ops, the way legacy skips pending notes),
   * and a text that differs from the base asks the next content pass for a
   * catch-up. A note this device does not have is inserted with the pulled
   * text as its base, at the pull's rev when it carries one (else 0: the
   * next pass catches it up).
   */
  private _applyNoteChangeV2(change: SyncChange, userId: string, catchUp: string[]): void {
    const { entityId, operation, data } = change;
    if (operation === "delete") {
      this._applyNoteChange(change, userId);
      return;
    }
    const existing = readEngineRow(entityId);
    const note = data as Record<string, unknown>;
    const content = (note.content ?? "") as string;
    if (!existing) {
      const createdAt = (note.createdAt ?? new Date().toISOString()) as string;
      const updatedAt = (note.updatedAt ?? new Date().toISOString()) as string;
      execute(
        `INSERT INTO notes (id, user_id, title, content, is_pinned, is_encrypted, encryption_hint, word_count, created_at, updated_at, deleted_at, archived_at, shared_at, sort_order, base_content, is_read_only, sync_status,
           rev, base_updated_at, pending_ops)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'synced', ?, ?, ?)`,
        [
          entityId,
          userId,
          note.title ?? "",
          content,
          note.isPinned ? 1 : 0,
          note.isEncrypted ? 1 : 0,
          note.encryptionHint ?? null,
          note.wordCount ?? 0,
          createdAt,
          updatedAt,
          note.deletedAt ?? null,
          note.archivedAt ?? null,
          note.sharedAt ?? null,
          (note.sortOrder ?? null) as number | null,
          content,
          note.isReadOnly ? 1 : 0,
          pulledRev(note),
          updatedAt,
          JSON.stringify(identityOp(content.length)),
        ],
      );
      writeSharingFields(entityId, note);
      replaceAttachmentProjectionForNote({ id: entityId, userId, content, createdAt, updatedAt });
      writeNoteLinks(entityId, extractNoteLinks(entityId, content));
      return;
    }
    writeSharingFields(entityId, note);
    if (!hasQueuedNoteOps(entityId)) {
      execute(
        `UPDATE notes SET is_pinned = ?, is_encrypted = ?, encryption_hint = ?, deleted_at = ?, archived_at = ?,
           shared_at = ?, sort_order = ?, is_read_only = ?
         WHERE id = ?`,
        [
          note.isPinned ? 1 : 0,
          note.isEncrypted ? 1 : 0,
          note.encryptionHint ?? null,
          note.deletedAt ?? null,
          note.archivedAt ?? null,
          note.sharedAt ?? null,
          (note.sortOrder ?? null) as number | null,
          note.isReadOnly ? 1 : 0,
          entityId,
        ],
      );
    }
    // Outside the engine (its create is queued): the create reply brings it in.
    if (existing.pending_ops !== null && content !== existing.base_content) catchUp.push(entityId);
  }

  private _applyTagChange(change: SyncChange, userId: string): void {
    const { entityId, operation, data } = change;

    if (operation === "delete") {
      execute("DELETE FROM note_tags WHERE tag_id = ?", [entityId]);
      execute("DELETE FROM tags WHERE id = ?", [entityId]);
      return;
    }

    const existingUserId =
      queryOne<{ user_id: string }>("SELECT user_id FROM tags WHERE id = ?", [entityId])?.user_id || userId;
    const tag = data as Record<string, unknown>;
    // In place, never REPLACE: a REPLACE deletes the row first and cascades
    // every note_tags link of the tag, which a pull from 0 (the level 3
    // re-pull) does not always bring back in the same page.
    execute(
      `INSERT INTO tags (id, user_id, name, parent_id, color, icon, is_pinned, created_at, sync_status)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'synced')
       ON CONFLICT(id) DO UPDATE SET user_id = excluded.user_id, name = excluded.name, parent_id = excluded.parent_id,
         color = excluded.color, icon = excluded.icon, is_pinned = excluded.is_pinned, created_at = excluded.created_at,
         sync_status = 'synced'`,
      [
        entityId,
        existingUserId,
        tag.name ?? "",
        tag.parentId ?? null,
        tag.color ?? null,
        tag.icon ?? null,
        tag.isPinned ? 1 : 0,
        tag.createdAt ?? new Date().toISOString(),
      ],
    );
  }

  private _applyNoteTagChange(change: SyncChange): void {
    const { operation, data } = change;
    const noteId = data.noteId as string;
    const tagId = data.tagId as string;

    if (operation === "delete") {
      execute("DELETE FROM note_tags WHERE note_id = ? AND tag_id = ?", [noteId, tagId]);
    } else {
      // Use a conditional INSERT: only insert when both FK targets exist.
      // INSERT OR IGNORE only handles UNIQUE violations, not FK failures,
      // and a FK failure inside a transaction rolls back ALL changes.
      execute(
        `INSERT OR IGNORE INTO note_tags (note_id, tag_id)
         SELECT ?, ?
         WHERE EXISTS (SELECT 1 FROM notes WHERE id = ?)
           AND EXISTS (SELECT 1 FROM tags WHERE id = ?)`,
        [noteId, tagId, noteId, tagId],
      );
    }
  }

  /**
   * Apply a shared_note_ref change pulled from the worker.
   *
   * On delete: drop both the pointer row and the local content cache row so
   * the "Shared with me" list and any open viewer update reactively.
   * On create: upsert the pointer row, leaving content cache to be filled
   * lazily by SharedNoteService.refresh() when the user opens the entry.
   */
  private _applySharedNoteRefChange(change: SyncChange, userId: string): void {
    const { entityId, operation, data } = change;

    if (operation === "delete") {
      const noteId = (data?.noteId as string | undefined) ?? null;
      execute("DELETE FROM shared_note_refs WHERE id = ?", [entityId]);
      if (noteId) {
        execute("DELETE FROM shared_notes WHERE id = ?", [noteId]);
      }
      return;
    }

    const noteId = (data?.noteId as string | undefined) ?? "";
    const addedAt = (data?.addedAt as string | undefined) ?? new Date().toISOString();
    if (!noteId) return;
    execute(
      `INSERT OR REPLACE INTO shared_note_refs (id, user_id, note_id, added_at, sync_status)
       VALUES (?, ?, ?, ?, 'synced')`,
      [entityId, userId, noteId, addedAt],
    );
  }
}
